import React, { useEffect, useState } from 'react';
import { useEditorStore } from '~/shell/Store';
import { useToastStore } from '~/ui/toast/toastStore';
import { ToastSeverity } from '~/ui/toast/types';
import BoldIcon from '~/assets/styleButton/bold.svg';
import ItalicIcon from '~/assets/styleButton/italic.svg';
import UnderlineIcon from '~/assets/styleButton/underline.svg';
import LinkIcon from '~/assets/styleButton/link.svg';
import RemoveLinkIcon from '~/assets/styleButton/trash.svg';
import FixSpellingIcon from '~/assets/icons/fix_spelling.png';
import MakeItShorterIcon from '~/assets/icons/make_longer.png';
import MakeItLongerIcon from '~/assets/icons/make_shorter.png';
import SimplifyWritingIcon from '~/assets/icons/make_simplify.png';
import PenEditStatementIcon from '~/assets/icons/PenEditStatement.svg';
import { Overlay } from '~/ui/overlay/Overlay';
import CircleLoader from '~/editor/loader/CircleLoader';
import styled from 'styled-components';
import RectangleIcon from './rectangle.png';
import ListIcon from '~/assets/styleButton/list.svg';
import ListOrderedIcon from '~/assets/styleButton/list-ordered.svg';
import { normalizeUrl } from '~/lib/normalizeUrl';
import { LinkPopup } from '~/ui/link-popup/LinkPopup';

import AlignLeftIcon from '~/assets/styleButton/align-left.svg';
import AlignCenterIcon from '~/assets/styleButton/align-center.svg';
import AlignRightIcon from '~/assets/styleButton/align-right.svg';
import AlignJustifyIcon from '~/assets/styleButton/align-justify.svg';

const LinkPopupContainer = styled.div`
  max-width: 170px;
`;

const Tooltip = styled.div<{
  showArrow?: boolean;
  minHeight?: string;
  width?: string;
}>`
  cursor: pointer;
  z-index: 2000;
  display: flex;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  background: #fff;
  border-radius: 9px;
  min-height: ${p => p.minHeight || '60px'};
  width: ${p => p.width || 'auto'};

  button {
    background: transparent;
    height: 32px;
    padding: 2px;
  }

  &:before {
    content: '';
    position: absolute;
    background-image: url(${RectangleIcon});
    background-size: 26px 26px;
    background-repeat: no-repeat;
    background-position: left;
    width: 30px;
    height: 30px;
    margin-top: -13px;
    margin-left: 30px;
    display: ${p => (p.showArrow ? 'block' : 'none')};
    z-index: -1;
  }

  &.loading:before {
    margin-left: 46px;
    margin-top: -6px;
  }

  .circle-loader {
    border-radius: 9px;
    background: #fff;
    padding: 12px 30px;
    margin-left: 120px;
  }

  ul {
    margin: 10px;
    li {
      list-style: none;
      background-size: 26px 26px;
      background-repeat: no-repeat;
      background-position: left;
      padding-left: 30px;
      padding-top: 6px;
      padding-bottom: 6px;
      margin-top: 2px;
      margin-bottom: 2px;
    }
    li.fixSpellingAndGrammar {
      background-image: url(${FixSpellingIcon});
    }
    li.makeItShorter {
      background-image: url(${MakeItLongerIcon});
    }
    li.makeItLonger {
      background-image: url(${MakeItShorterIcon});
    }
    li.simplifyWriting {
      background-image: url(${SimplifyWritingIcon});
    }
  }

  a {
    color: #9738ff;
    font-family: ralewayBold;
    font-weight: unset !important;
    font-size: 14px;
    line-height: 150%;
    letter-spacing: 0%;
    text-align: center;
    font-variant-numeric-figure: lining-nums;
    font-variant-numeric-spacing: proportional-nums;
  }

  .style-buttons {
    margin-left: 5px;
  }

  .text-style-formatting {
    margin-left: 5px;
    padding-left: 5px;

    button {
      padding: 0px;
    }
  }
`;

Tooltip.defaultProps = {
  showArrow: true,
  minHeight: '60px',
  width: 'auto'
};

export const RewriteWithAI = ({
  id,
  selectedText,
  savedRange,
  onRangeChange,
  clearRange,
  variant,
  textAlignment = true,
  canUseMarkTag = false
}: {
  id: string;
  selectedText: string;
  savedRange: Range | null;
  onRangeChange: (range: Range | null) => void;
  variant?: string;
  clearRange: () => void;
  textAlignment?: boolean;
  canUseMarkTag?: boolean;
}) => {
  const { apiService } = useEditorStore();
  const [isLoading, setIsLoading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [url, setUrl] = useState('');
  const [hasLink, setHasLink] = useState(false);
  const [hasMark, setHasMark] = useState(false);

  const [part1, part2] = id.split('_');
  const elementType = isNaN(Number(part1)) ? part1 : part2;

  const maxLengthByType: Record<string, number> = {
    title: 512
  };

  const maxLength = maxLengthByType[elementType];

  const onClick = (type: string, event: React.MouseEvent) => {
    event.preventDefault();
    setIsLoading(true);
    const selection = window.getSelection();
    const container =
      selection?.anchorNode?.parentElement?.closest('[data-id]');
    const fullTextState = container?.textContent ?? '';

    apiService
      ?.rewriteSubItem({
        text: selectedText,
        instructions: type,
        full_text: fullTextState,
        ...(maxLength !== undefined && { max_length: maxLength })
      })
      .then(res => {
        if (res.error) {
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            range.deleteContents();
          }

          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: res.message
          });
        }

        if (res.content) {
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            range.deleteContents();
            range.insertNode(document.createTextNode(res.content));
          }
        }
      })
      .catch(err => {
        setIsLoading(false);
        clearRange();
        useToastStore.getState().add({
          severity: ToastSeverity.Error,
          message: 'Error: ' + err.message
        });
      });

    return false;
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;

    if (
      (target.closest('button') ||
        target.closest('a') ||
        target.closest('svg')) &&
      !target.closest('input')
    ) {
      event.preventDefault();
    }

    if (target.closest('input')) {
      event.stopPropagation();
    }
  };

  const formatText = (command: string) => {
    if (document) {
      try {
        // Special handling for mark tag
        if (command === 'mark') {
          const selection = window.getSelection();
          let range: Range | null = null;

          // Use savedRange if available, otherwise get current selection
          if (savedRange) {
            range = savedRange;
          } else if (selection && selection.rangeCount > 0) {
            range = selection.getRangeAt(0);
          }

          if (range) {
            const selectedText = range.toString();
            if (!selectedText.trim()) {
              // If no text selected, try to use execCommand as fallback
              document.execCommand(command, false);
              return;
            }

            // Check if selection is already wrapped in mark tag
            let container = range.commonAncestorContainer;
            if (container.nodeType === Node.TEXT_NODE) {
              container = container.parentNode as Node;
            }
            if (container && (container as Element).tagName === 'MARK') {
              // If already marked, unwrap it
              const markElement = container as Element;
              const parent = markElement.parentNode;
              if (parent) {
                while (markElement.firstChild) {
                  parent.insertBefore(markElement.firstChild, markElement);
                }
                parent.removeChild(markElement);
              }
              clearRange();
              return;
            }

            // Create mark element and wrap selected text
            const markElement = document.createElement('mark');
            try {
              const contents = range.extractContents();
              markElement.appendChild(contents);
              range.insertNode(markElement);
            } catch (e) {
              // Fallback: wrap the range
              range.surroundContents(markElement);
            }

            // Clear selection
            if (selection) {
              selection.removeAllRanges();
            }
            clearRange();
          } else {
            // Fallback to execCommand if no selection
            document.execCommand(command, false);
          }
        } else {
          // Standard commands
          document.execCommand(command, false);
        }
      } catch (error) {
        console.error('Error executing command:', command, error);
      }
    }
  };

  const createListType = (listType: 'ul' | 'ol') => {
    if (document && savedRange) {
      try {
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(savedRange);

          const selectedText = savedRange.toString();
          if (!selectedText.trim()) return;

          const lines = selectedText.split('\n').filter(line => line.trim());
          const listElement = document.createElement(listType);

          lines.forEach(line => {
            const listItem = document.createElement('li');
            listItem.textContent = line.trim();
            if (listElement) {
              listElement.appendChild(listItem);
            }
          });

          savedRange.deleteContents();
          savedRange.insertNode(listElement);

          clearRange();
        }
      } catch (error) {
        useToastStore.getState().add({
          severity: ToastSeverity.Error,
          message: 'Error creating list: ' + error.message
        });
      }
    }
  };

  const createListFallback = (listType: 'ul' | 'ol') => {
    if (document && savedRange) {
      try {
        const selection = window.getSelection();

        if (selection) {
          selection.removeAllRanges();
          selection.addRange(savedRange);

          const commands = [
            listType === 'ul' ? 'insertUnorderedList' : 'insertOrderedList',
            'insertList',
            listType === 'ul' ? 'formatBlock' : 'formatBlock'
          ];

          let success = false;

          for (const command of commands) {
            try {
              if (command === 'formatBlock') {
                document.execCommand(
                  command,
                  false,
                  listType === 'ul' ? '<ul>' : '<ol>'
                );
              } else {
                document.execCommand(command, false);
              }

              success = true;

              break;
            } catch (e) {
              // Fallback to default list creation
            }
          }

          if (!success) {
            createListType(listType);
          } else {
            clearRange();
          }
        }
      } catch (error) {
        createListType(listType);
      }
    }
  };

  const alignText = (alignment: 'left' | 'center' | 'right' | 'justify') => {
    if (document && savedRange) {
      try {
        const selection = window.getSelection();

        if (selection) {
          selection.removeAllRanges();
          selection.addRange(savedRange);

          try {
            if (alignment === 'left') {
              document.execCommand('justifyLeft', false);
            } else if (alignment === 'center') {
              document.execCommand('justifyCenter', false);
            } else if (alignment === 'right') {
              document.execCommand('justifyRight', false);
            } else if (alignment === 'justify') {
              document.execCommand('justifyFull', false);
            }

            const selectedElement = selection.anchorNode?.parentElement;

            if (selectedElement) {
              selectedElement.style.textAlign = alignment;
              selectedElement.removeAttribute('class');
            }

            clearRange();
          } catch (e) {
            const selectedText = savedRange.toString();
            if (selectedText.trim()) {
              const alignedElement = document.createElement('div');
              alignedElement.style.textAlign = alignment;
              alignedElement.textContent = selectedText;

              savedRange.deleteContents();
              savedRange.insertNode(alignedElement);
              clearRange();
            }
          }
        }
      } catch (error) {
        useToastStore.getState().add({
          severity: ToastSeverity.Error,
          message: 'Error aligning text: ' + error.message
        });
      }
    }
  };

  // Helper function to check if element is a link (not skip-link)
  const isLinkElement = (element: Element | null): boolean => {
    return (
      element !== null &&
      element.tagName === 'A' &&
      !element.classList.contains('skip-link')
    );
  };

  const getLinkInfo = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0)
      return { hasLink: false, url: '' };

    try {
      const range = selection.getRangeAt(0);
      const fragment = range.cloneContents();
      const tempDiv = document.createElement('div');
      if (tempDiv && fragment) {
        tempDiv.appendChild(fragment);
      }

      const linkElement = tempDiv.querySelector('a');
      if (isLinkElement(linkElement) && linkElement) {
        return {
          hasLink: true,
          url: linkElement.getAttribute('href') || ''
        };
      }

      let node: Node | null = range.startContainer;
      while (node && node !== document.body) {
        if (
          node.nodeType === Node.ELEMENT_NODE &&
          isLinkElement(node as Element)
        ) {
          return {
            hasLink: true,
            url: (node as Element).getAttribute('href') || ''
          };
        }
        node = node.parentNode;
      }

      node = range.endContainer;
      while (node && node !== document.body) {
        if (
          node.nodeType === Node.ELEMENT_NODE &&
          isLinkElement(node as Element)
        ) {
          return {
            hasLink: true,
            url: (node as Element).getAttribute('href') || ''
          };
        }
        node = node.parentNode;
      }

      if (
        range.startContainer === range.endContainer &&
        range.startContainer.nodeType === Node.TEXT_NODE
      ) {
        const parent = range.startContainer.parentElement;
        if (isLinkElement(parent) && parent) {
          return {
            hasLink: true,
            url: parent.getAttribute('href') || ''
          };
        }
      }

      const commonAncestor = range.commonAncestorContainer;
      if (commonAncestor.nodeType === Node.ELEMENT_NODE) {
        const links = (commonAncestor as Element).querySelectorAll('a');
        for (const link of links) {
          if (!isLinkElement(link)) continue;
          try {
            if (range.intersectsNode(link)) {
              return {
                hasLink: true,
                url: link.getAttribute('href') || ''
              };
            }
          } catch (e) {
            const linkRange = document.createRange();
            linkRange.selectNodeContents(link);

            const startComparison = range.compareBoundaryPoints(
              Range.START_TO_END,
              linkRange
            );
            const endComparison = range.compareBoundaryPoints(
              Range.END_TO_START,
              linkRange
            );

            if (startComparison <= 0 && endComparison >= 0) {
              return {
                hasLink: true,
                url: link.getAttribute('href') || ''
              };
            }
          }
        }
      }

      return { hasLink: false, url: '' };
    } catch (error) {
      return { hasLink: false, url: '' };
    }
  };

  const hasLinkInSelection = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return false;

    try {
      const range = selection.getRangeAt(0);

      const fragment = range.cloneContents();
      const tempDiv = document.createElement('div');
      if (tempDiv && fragment) {
        tempDiv.appendChild(fragment);
      }

      const linkElement = tempDiv.querySelector('a');
      if (isLinkElement(linkElement)) {
        return true;
      }

      let node: Node | null = range.startContainer;
      while (node && node !== document.body) {
        if (
          node.nodeType === Node.ELEMENT_NODE &&
          isLinkElement(node as Element)
        ) {
          return true;
        }
        node = node.parentNode;
      }

      node = range.endContainer;
      while (node && node !== document.body) {
        if (
          node.nodeType === Node.ELEMENT_NODE &&
          isLinkElement(node as Element)
        ) {
          return true;
        }
        node = node.parentNode;
      }

      const startContainer = range.startContainer;
      const endContainer = range.endContainer;

      if (
        startContainer === endContainer &&
        startContainer.nodeType === Node.TEXT_NODE
      ) {
        const parent = startContainer.parentElement;
        if (isLinkElement(parent)) {
          return true;
        }
      }

      const commonAncestor = range.commonAncestorContainer;
      if (commonAncestor.nodeType === Node.ELEMENT_NODE) {
        const links = (commonAncestor as Element).querySelectorAll('a');
        for (const link of links) {
          if (!isLinkElement(link)) continue;
          try {
            if (range.intersectsNode(link)) {
              return true;
            }
          } catch (e) {
            const linkRange = document.createRange();
            linkRange.selectNodeContents(link);

            const startComparison = range.compareBoundaryPoints(
              Range.START_TO_END,
              linkRange
            );
            const endComparison = range.compareBoundaryPoints(
              Range.END_TO_START,
              linkRange
            );

            if (startComparison <= 0 && endComparison >= 0) {
              return true;
            }
          }
        }
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  const hasMarkInSelection = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return false;

    try {
      const range = selection.getRangeAt(0);

      const fragment = range.cloneContents();
      const tempDiv = document.createElement('div');
      if (tempDiv && fragment) {
        tempDiv.appendChild(fragment);
      }

      const markElement = tempDiv.querySelector('mark');
      if (markElement) {
        return true;
      }

      let node: Node | null = range.startContainer;
      while (node && node !== document.body) {
        if (
          node.nodeType === Node.ELEMENT_NODE &&
          (node as Element).tagName === 'MARK'
        ) {
          return true;
        }
        node = node.parentNode;
      }

      node = range.endContainer;
      while (node && node !== document.body) {
        if (
          node.nodeType === Node.ELEMENT_NODE &&
          (node as Element).tagName === 'MARK'
        ) {
          return true;
        }
        node = node.parentNode;
      }

      const startContainer = range.startContainer;
      const endContainer = range.endContainer;

      if (
        startContainer === endContainer &&
        startContainer.nodeType === Node.TEXT_NODE
      ) {
        const parent = startContainer.parentElement;
        if (parent && parent.tagName === 'MARK') {
          return true;
        }
      }

      const commonAncestor = range.commonAncestorContainer;
      if (commonAncestor.nodeType === Node.ELEMENT_NODE) {
        const marks = (commonAncestor as Element).querySelectorAll('mark');
        for (const mark of marks) {
          try {
            if (range.intersectsNode(mark)) {
              return true;
            }
          } catch (e) {
            const markRange = document.createRange();
            markRange.selectNodeContents(mark);

            const startComparison = range.compareBoundaryPoints(
              Range.START_TO_END,
              markRange
            );
            const endComparison = range.compareBoundaryPoints(
              Range.END_TO_START,
              markRange
            );

            if (startComparison <= 0 && endComparison >= 0) {
              return true;
            }
          }
        }
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  const handleAddLink = (linkUrl?: string) => {
    const targetUrl = linkUrl || url;
    if (targetUrl && document && savedRange) {
      try {
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(savedRange);

          const normalizedUrl = normalizeUrl(targetUrl);
          document.execCommand('createLink', false, normalizedUrl);

          const link = selection?.anchorNode?.parentElement;
          if (link && link.tagName === 'A') {
            link.setAttribute('target', '_blank');
          }
        }

        setTimeout(() => {
          setUrl('');
          setShowUrlInput(false);
          onRangeChange(null);
          clearRange();
        }, 0);
      } catch (error) {
        useToastStore.getState().add({
          severity: ToastSeverity.Error,
          message: 'Error adding link: ' + error.message
        });
      }
    }
  };

  const handleRemoveLink = () => {
    if (document) {
      try {
        document.execCommand('unlink', false);
        document.execCommand('removeFormat', false);
        clearRange();
      } catch (error) {
        console.error('Error removing link:', error);
        useToastStore.getState().add({
          severity: ToastSeverity.Error,
          message: 'Error removing link: ' + error.message
        });
      }
    }
  };

  const handleRemoveMark = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    try {
      const range = selection.getRangeAt(0);
      const marksToUnwrap: Element[] = [];

      // First, check if start/end containers or their parents are inside mark tags
      // This handles the case when selection is completely inside a mark element
      // (like when user selects only "test1" from "<mark>test1</mark>")
      let node: Node | null = range.startContainer;
      while (node && node !== document.body) {
        if (
          node.nodeType === Node.ELEMENT_NODE &&
          (node as Element).tagName === 'MARK'
        ) {
          if (!marksToUnwrap.includes(node as Element)) {
            marksToUnwrap.push(node as Element);
          }
        }
        node = node.parentNode;
      }

      node = range.endContainer;
      while (node && node !== document.body) {
        if (
          node.nodeType === Node.ELEMENT_NODE &&
          (node as Element).tagName === 'MARK'
        ) {
          if (!marksToUnwrap.includes(node as Element)) {
            marksToUnwrap.push(node as Element);
          }
        }
        node = node.parentNode;
      }

      // Also check for mark elements that intersect with the range
      // This handles cases when selection spans across mark boundaries
      const commonAncestor = range.commonAncestorContainer;
      if (commonAncestor.nodeType === Node.ELEMENT_NODE) {
        const marks = (commonAncestor as Element).querySelectorAll('mark');
        for (const mark of marks) {
          // Skip if already found via parent traversal
          if (marksToUnwrap.includes(mark)) continue;

          try {
            if (range.intersectsNode(mark)) {
              marksToUnwrap.push(mark);
            }
          } catch (e) {
            const markRange = document.createRange();
            markRange.selectNodeContents(mark);
            const startComparison = range.compareBoundaryPoints(
              Range.START_TO_END,
              markRange
            );
            const endComparison = range.compareBoundaryPoints(
              Range.END_TO_START,
              markRange
            );
            // If range overlaps with mark in any way
            if (
              (startComparison >= 0 && endComparison <= 0) ||
              (startComparison <= 0 && endComparison >= 0)
            ) {
              marksToUnwrap.push(mark);
            }
          }
        }
      }

      // Unwrap all mark elements
      if (marksToUnwrap.length > 0) {
        marksToUnwrap.forEach(mark => {
          const parent = mark.parentNode;
          if (parent) {
            while (mark.firstChild) {
              parent.insertBefore(mark.firstChild, mark);
            }
            parent.removeChild(mark);
          }
        });

        clearRange();
      }
    } catch (error) {
      console.error('Error removing mark:', error);
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Error removing mark: ' + error.message
      });
    }
  };

  const handleRemoveStyles = () => {
    handleRemoveLink();
    handleRemoveMark();
  };

  const handleMouseUp = () => {
    setTimeout(() => {
      setHasLink(hasLinkInSelection());
      setHasMark(hasMarkInSelection());
    }, 10);
  };

  const handleDoubleClick = () => {
    setTimeout(() => {
      setHasLink(hasLinkInSelection());
      setHasMark(hasMarkInSelection());
    }, 50);
  };

  useEffect(() => {
    const handleSelectionChange = () => {
      setTimeout(() => {
        setHasLink(hasLinkInSelection());
        setHasMark(hasMarkInSelection());
      }, 10);
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('dblclick', handleDoubleClick);

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('dblclick', handleDoubleClick);
    };
  }, []);

  return (
    <div
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      data-rewrite-portal="true"
    >
      {!isLoading && (
        <Tooltip
          minHeight="36px"
          width="fit-content"
          style={{
            minWidth: '226px',
            display: 'flex',
            flexDirection: 'column'
          }}
          showArrow={false}
        >
          <div
            className="style-buttons"
            style={{ display: 'flex', flexDirection: 'row' }}
          >
            {!showUrlInput && (
              <button onClick={() => formatText('bold')}>
                <BoldIcon />
              </button>
            )}
            {!showUrlInput && (
              <button onClick={() => formatText('italic')}>
                <ItalicIcon />
              </button>
            )}
            {!showUrlInput && (
              <button
                onClick={() => formatText('underline')}
                style={{ marginLeft: '-3px' }}
              >
                <UnderlineIcon />
              </button>
            )}

            {!showUrlInput && canUseMarkTag && (
              <button
                onClick={() => formatText('mark')}
                style={{ marginLeft: '3px', marginRight: '6px' }}
              >
                <div
                  style={{
                    width: '20px',
                    height: '20px'
                  }}
                >
                  <PenEditStatementIcon
                    style={{
                      marginLeft: '-6px',
                      marginTop: '-6px'
                    }}
                  />
                </div>
              </button>
            )}

            {!showUrlInput && (
              <button
                onClick={() => setShowUrlInput(true)}
                style={{ marginLeft: '-3px' }}
              >
                <LinkIcon />
              </button>
            )}

            {!showUrlInput && hasLink && (
              <LinkPopupContainer>
                <LinkPopup
                  url={getLinkInfo().url}
                  showIcon={false}
                  hideDeleteButton
                  onEditClick={() => {
                    setShowUrlInput(true);
                    setUrl(getLinkInfo().url);
                  }}
                />
              </LinkPopupContainer>
            )}
            {!showUrlInput && (hasLink || hasMark) && (
              <button
                onClick={handleRemoveStyles}
                style={{ marginTop: '-2px' }}
              >
                <RemoveLinkIcon />
              </button>
            )}
          </div>

          {!showUrlInput && variant == 'text' && textAlignment && (
            <div className="text-style-formatting">
              {!showUrlInput && variant == 'text' && (
                <button onClick={() => alignText('left')}>
                  <AlignLeftIcon />
                </button>
              )}
              {!showUrlInput && variant == 'text' && (
                <button onClick={() => alignText('center')}>
                  <AlignCenterIcon />
                </button>
              )}
              {!showUrlInput && variant == 'text' && (
                <button onClick={() => alignText('right')}>
                  <AlignRightIcon />
                </button>
              )}
              {!showUrlInput && variant == 'text' && (
                <button onClick={() => alignText('justify')}>
                  <AlignJustifyIcon />
                </button>
              )}
              {!showUrlInput && variant == 'text' && (
                <button
                  onClick={() => createListFallback('ul')}
                  style={{ minWidth: '32px' }}
                >
                  <ListIcon />
                </button>
              )}
              {!showUrlInput && variant == 'text' && (
                <button
                  onClick={() => createListFallback('ol')}
                  style={{ minWidth: '32px' }}
                >
                  <ListOrderedIcon />
                </button>
              )}
            </div>
          )}

          {showUrlInput && (
            <LinkPopup
              url={url}
              showIcon={false}
              forceEditMode
              placeholder="Enter URL"
              onUrlChange={handleAddLink}
              onCancel={() => {
                setShowUrlInput(false);
                setUrl('');
              }}
            />
          )}
        </Tooltip>
      )}
      {!showUrlInput && !isLoading && (
        <Tooltip
          className={`${isLoading ? 'loading' : ''}`}
          style={{ marginTop: '12px' }}
          showArrow={false}
          width="226px"
        >
          {!isLoading && (
            <ul>
              <li className="fixSpellingAndGrammar">
                <a
                  href="#"
                  onClick={e => onClick('Fix Spelling and Grammar', e)}
                  className="skip-link"
                >
                  Fix Spelling and Grammar
                </a>
              </li>
              <li className="makeItShorter">
                <a
                  href="#"
                  onClick={e => onClick('Make it Shorter', e)}
                  className="skip-link"
                >
                  Make it Shorter
                </a>
              </li>
              <li className="makeItLonger">
                <a
                  href="#"
                  onClick={e => onClick('Make it Longer', e)}
                  className="skip-link"
                >
                  Make it Longer
                </a>
              </li>
              <li className="simplifyWriting">
                <a
                  href="#"
                  onClick={e => onClick('Simplify writing', e)}
                  className="skip-link"
                >
                  Simplify writing
                </a>
              </li>
            </ul>
          )}
        </Tooltip>
      )}
      {isLoading && (
        <Tooltip
          minHeight="36px"
          width="fit-content"
          showArrow={false}
          style={{
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
            minHeight: '36px',
            backgroundColor: 'transparent',
            maxWidth: '100px',
            boxShadow: 'none'
          }}
        >
          <Overlay open={true} opacity={0.2}></Overlay>
          <CircleLoader />
        </Tooltip>
      )}
    </div>
  );
};
